import { useState, useRef, useLayoutEffect } from "react";
import { TextField } from "@mui/material";
import {
  getLocaleConfig,
  parseLocalizedNumber,
  formatInputWithSeparators,
} from "../../utils/numeros.js";

/**
 * Input numérico con formateo regional (separador de miles / decimal) que SÍ
 * permite escribir decimales y estados parciales mientras se tipea
 * (ej: "1.500,"  o  "1.500,50"  o  "1.500,0").
 *
 * Por qué existe: un <TextField value={formatInputWithSeparators(numero)}> se
 * re-deriva del NÚMERO en cada render, y un número no puede representar la coma
 * colgante ni los ceros a la derecha → se borra el decimal apenas se tipea.
 * Acá guardamos internamente el STRING que se muestra (displayValue) y hacia
 * afuera entregamos el número parseado vía onChange(numero). Así el padre sigue
 * trabajando con números y no hay que tocar su lógica de cálculo.
 *
 * Props:
 *  - value: número (o string numérico, o "") del estado del padre.
 *  - onChange: (numero|"") => void   recibe el número parseado, no el evento.
 *  - locale: locale para los separadores (ej "es-AR").
 *  - ...rest: cualquier prop extra se pasa al <TextField> (size, label, sx,
 *             InputProps, fullWidth, disabled, placeholder, variant, etc.).
 */
export default function NumericInput({ value, onChange, locale = null, ...rest }) {
  const { thousandSeparator, decimalSeparator } = getLocaleConfig(locale);
  const inputRef = useRef(null);
  const pendingCursor = useRef(null);

  // Forma canónica (formateada) del valor que viene del padre.
  const externalDisplay =
    value === "" || value === null || value === undefined
      ? ""
      : formatInputWithSeparators(value, locale);

  const [displayValue, setDisplayValue] = useState(externalDisplay);

  // Re-sincronizar el display cuando el número externo cambia desde afuera
  // (carga inicial, reset, o un cálculo como costo↔margen↔precio) SIN pisar lo
  // que el usuario está tipeando: comparamos formas canónicas, así "1.500," (que
  // representa 1500) no se considera distinto de 1500 y la coma sobrevive.
  const shownNumber = parseLocalizedNumber(displayValue, locale);
  const shownDisplay = shownNumber == null ? "" : formatInputWithSeparators(shownNumber, locale);
  if (shownDisplay !== externalDisplay) {
    setDisplayValue(externalDisplay);
  }

  // Restaurar la posición del cursor después de re-formatear (solo tras tipear).
  useLayoutEffect(() => {
    if (pendingCursor.current != null && inputRef.current) {
      inputRef.current.setSelectionRange(pendingCursor.current, pendingCursor.current);
      pendingCursor.current = null;
    }
  });

  const handleChange = (e) => {
    const el = e.target;
    const raw = el.value;
    const cursorPos = el.selectionStart;

    if (raw === "") {
      setDisplayValue("");
      onChange("");
      return;
    }

    const parsed = parseLocalizedNumber(raw, locale);
    if (parsed === null) return; // entrada no parseable: ignorar, mantener el valor anterior

    let formatted = formatInputWithSeparators(parsed, locale);

    // Preservar el separador decimal final mientras se escribe ("1500," → "1.500,")
    if (raw.endsWith(decimalSeparator) && !formatted.endsWith(decimalSeparator)) {
      formatted += decimalSeparator;
    }
    // Preservar la parte decimal parcial y los ceros a la derecha ("1500,50" → "1.500,50")
    if (raw.includes(decimalSeparator)) {
      const fraction = raw.split(decimalSeparator)[1].replace(/\D/g, "").slice(0, 6);
      formatted = formatted.split(decimalSeparator)[0] + decimalSeparator + fraction;
    }

    // Recalcular el cursor contando solo caracteres reales (ignorando separadores de miles).
    const realBefore = raw.substring(0, cursorPos).split(thousandSeparator).join("").length;
    let newPos = formatted.length;
    if (realBefore > 0) {
      let count = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (formatted[i] !== thousandSeparator) {
          count++;
          if (count >= realBefore) {
            newPos = i + 1;
            break;
          }
        }
      }
    }
    pendingCursor.current = newPos;

    setDisplayValue(formatted);
    onChange(parsed);
  };

  return (
    <TextField {...rest} value={displayValue} onChange={handleChange} inputRef={inputRef} />
  );
}
